// Staff feats view - feat management

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { slugToTitle } from '../../lib/format.js';
import { router } from '../../lib/router.js';

// Pencil icon SVG
const pencilIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path
    d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"
  />
</svg>`;

// Trash icon SVG
const trashIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M3 6h18" />
  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
</svg>`;

// Clipboard icon SVG
const clipboardIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
</svg>`;

/**
 * Staff feats view component for managing feats
 */
class StaffFeatsView extends BaseComponent {
  static properties = {
    _feats: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _showDeleteModal: { type: Boolean, state: true },
    _featToDelete: { type: String, state: true },
    _categoryFilter: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Feat>} */
    this._feats = [];
    this._loading = true;
    this._error = null;
    this._showDeleteModal = false;
    this._featToDelete = null;
    this._categoryFilter = '';
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadFeats();
  }

  async _loadFeats() {
    this._loading = true;
    this._error = null;

    try {
      this._feats = await api.get('/api/staff/feats');
    } catch (err) {
      console.error('Failed to load feats:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load feats';
    } finally {
      this._loading = false;
    }
  }

  get _filteredFeats() {
    if (!this._categoryFilter) return this._feats;
    return this._feats.filter((f) => f.category === this._categoryFilter);
  }

  _goToNewFeat() {
    router.navigate('/staff/feats/new');
  }

  /** @param {string} slug */
  _editFeat(slug) {
    router.navigate(`/staff/feats/${slug}/edit`);
  }

  /** @param {string} slug */
  _deleteFeat(slug) {
    this._featToDelete = slug;
    this._showDeleteModal = true;
  }

  async _confirmDelete() {
    if (!this._featToDelete) return;

    try {
      await api.delete(`/api/staff/feats/${this._featToDelete}`);
      this._featToDelete = null;
      this._showDeleteModal = false;
      await this._loadFeats();
    } catch (err) {
      console.error('Failed to delete feat:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to delete feat';
    }
  }

  _cancelDelete() {
    this._showDeleteModal = false;
    this._featToDelete = null;
  }

  /** @param {string} category */
  _formatCategory(category) {
    return slugToTitle(category);
  }

  /**
   * @param {string} description
   * @param {number} [maxLength]
   */
  _truncateDescription(description, maxLength = 100) {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return `${description.substring(0, maxLength)}...`;
  }

  /**
   * @param {string} text
   * @param {Event} event
   */
  _copyToClipboard(text, event) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        const btn = /** @type {HTMLElement} */ (event.target).closest('button');
        if (btn) {
          btn.classList.add('btn-success');
          btn.classList.remove('btn-outline-secondary');
          setTimeout(() => {
            btn.classList.remove('btn-success');
            btn.classList.add('btn-outline-secondary');
          }, 2000);
        }
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }

  /** @param {Event} e */
  _handleCategoryChange(e) {
    this._categoryFilter = /** @type {HTMLSelectElement} */ (e.target).value;
  }

  render() {
    return html`
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">Staff</li>
          <li class="breadcrumb-item active" aria-current="page">Feats</li>
        </ol>
      </nav>

      ${this._error ? html` <div class="alert alert-danger">${this._error}</div> ` : ''}

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Feats</h2>
        <div class="d-flex gap-2 align-items-center">
          <select
            class="form-select form-select-sm"
            style="width: auto;"
            .value="${this._categoryFilter}"
            @change="${this._handleCategoryChange}"
          >
            <option value="">All Categories</option>
            <option value="origin">Origin</option>
            <option value="fighting-style">Fighting Style</option>
            <option value="general">General</option>
            <option value="epic-boon">Epic Boon</option>
          </select>
          <button class="btn btn-primary" @click="${this._goToNewFeat}">Create Feat</button>
        </div>
      </div>

      ${
        this._loading
          ? html`
            <div class="text-center py-4">
              <div class="spinner-border">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          `
          : this._filteredFeats.length === 0
            ? html` <p class="text-muted">No feats yet.</p> `
            : html`
              <div class="d-flex flex-wrap gap-3">
                ${this._filteredFeats.map(
                  (feat) => html`
                    <div style="flex: 1 1 280px; min-width: 260px; max-width: 320px;">
                      <div class="card h-100">
                        <div class="card-body">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <h6 class="card-title mb-0">${feat.name}</h6>
                            <div class="d-flex gap-1">
                              <button
                                class="btn btn-sm btn-outline-primary"
                                title="Edit"
                                @click="${() => this._editFeat(feat.slug)}"
                              >
                                ${pencilIcon}
                              </button>
                              <button
                                class="btn btn-sm btn-outline-danger"
                                title="Delete"
                                @click="${() => this._deleteFeat(feat.slug)}"
                              >
                                ${trashIcon}
                              </button>
                            </div>
                          </div>

                          <div class="d-flex align-items-center gap-2 mb-2">
                            <code class="small text-muted">${feat.slug}</code>
                            <button
                              class="btn btn-sm btn-outline-secondary py-0 px-2"
                              title="Copy Slug"
                              @click="${(/** @type {Event} */ e) => this._copyToClipboard(feat.slug, e)}"
                            >
                              ${clipboardIcon}
                            </button>
                          </div>

                          <div class="mb-2">
                            <div class="d-flex gap-1 mb-2">
                              <span class="badge bg-info text-capitalize"
                                >${this._formatCategory(feat.category || '')}</span
                              >
                              <span class="badge bg-secondary">v${feat.version || 1}</span>
                              ${
                                feat.published
                                  ? html` <span class="badge bg-success">Published</span> `
                                  : html` <span class="badge bg-warning text-dark">Draft</span> `
                              }
                            </div>
                          </div>

                          ${
                            feat.description
                              ? html`
                                <p class="small text-muted mb-0">${this._truncateDescription(feat.description)}</p>
                              `
                              : ''
                          }
                        </div>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
      }

      <!-- Delete Confirmation Modal -->
      ${
        this._showDeleteModal
          ? html`
            <div class="modal show d-block" style="background: rgba(0,0,0,0.5);">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Delete Feat</h5>
                    <button type="button" class="btn-close" @click="${this._cancelDelete}"></button>
                  </div>
                  <div class="modal-body">
                    <p>Are you sure you want to delete this feat? This cannot be undone.</p>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="${this._cancelDelete}">Cancel</button>
                    <button type="button" class="btn btn-danger" @click="${this._confirmDelete}">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          `
          : ''
      }
    `;
  }
}

customElements.define('staff-feats-view', StaffFeatsView);
